import { useState, useRef } from "react";
import { Modal, IconButton, Box } from "@mui/material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import userProfile from "assets/images/user-profile.png";
import EditIcon from "@mui/icons-material/Edit";
import { addModerator } from "services/moderatorService";
import ArgonSelect from "components/ArgonSelect";
import { toast } from "react-toastify";
import { ROLE_OPTIONS } from "../consts";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  minWidth: 500,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: "16px",
  py: 5,
  px: 8,
};

function ModeratorModal({ open, handleClose, onUpdate }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    avatar: userProfile,
    role: "",
  });
  const [avatarPreview, setAvatarPreview] = useState(userProfile);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef();

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    handleChange(name, value);
  };

  const handleSelectChange = (field) => (option) => {
    handleChange(field, option);
  };

  const handleAvatarClick = () => {
    fileInputRef.current.click();
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setAvatarPreview(ev.target.result);
        handleChange("avatar", ev.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please fill all the fields");
      return;
    }

    const payload = new FormData();
    payload.append("name", form.name);
    payload.append("email", form.email);
    payload.append("role", form.role.value);
    // If a file is selected, append it
    const file = fileInputRef.current?.files[0];
    if (file) {
      payload.append("avatar", file);
    }
    try {
      setLoading(true);
      await addModerator(payload);
      toast.success("User added successfully");
      handleClose();
      onUpdate();
      setLoading(false);
    } catch (err) {
      toast.error(err.response.data.message);
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        <form onSubmit={handleSubmit}>
          <ArgonBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            mb={3}
          >
            <ArgonTypography variant="h4" fontWeight="medium" mb={2}>
              Add New User
            </ArgonTypography>
            <Box position="relative" mb={2}>
              <img
                src={avatarPreview}
                alt="avatar"
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: "12px",
                  objectFit: "cover",
                }}
              />
              <IconButton
                onClick={handleAvatarClick}
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  bgcolor: "#2d3f50",
                  borderRadius: "12px",
                }}
                size="small"
              >
                <EditIcon fontSize="small" color="white" />
              </IconButton>
              <input
                type="file"
                accept="image/jpeg, image/png, image/jpg"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleAvatarChange}
              />
            </Box>
          </ArgonBox>
          <ArgonBox mb={2}>
            <ArgonTypography variant="h6" fontWeight="bold" mb={1}>
              Name
            </ArgonTypography>
            <ArgonInput
              name="name"
              value={form.name}
              onChange={handleInputChange}
              fullWidth
              required
            />
          </ArgonBox>
          <ArgonBox mb={3}>
            <ArgonTypography variant="h6" fontWeight="bold" mb={1}>
              Email
            </ArgonTypography>
            <ArgonInput
              name="email"
              type="email"
              value={form.email}
              onChange={handleInputChange}
              fullWidth
              required
            />
          </ArgonBox>
          <ArgonBox mb={2}>
            <ArgonTypography variant="h6" fontWeight="bold" mb={1}>
              Role
            </ArgonTypography>
            <ArgonSelect
              name="role"
              value={form.role}
              onChange={handleSelectChange("role")}
              options={ROLE_OPTIONS}
              placeholder="Select Role"
              disable={loading}
              required
            />
          </ArgonBox>
          <ArgonBox display="flex" justifyContent="center" gap={2}>
            <ArgonButton
              variant="outlined"
              color="secondary"
              onClick={handleClose}
              type="button"
            >
              Cancel
            </ArgonButton>
            <ArgonButton
              variant="contained"
              color="primary"
              type="submit"
              disabled={loading}
            >
              {loading ? "Adding..." : "Add"}
            </ArgonButton>
          </ArgonBox>
        </form>
      </Box>
    </Modal>
  );
}

export default ModeratorModal;
